// Cross-batch capture session recovery for the `/capture/items` POST dispatch.
// Extracted verbatim from connector-dispatch-create-item.ts (no behaviour change)
// to keep that dispatcher under the 500-line cap. The 404 not_found branch calls
// this to re-home a count whose frozen session id is absent server-side.

import type { DispatchContext } from './connector-dispatch-context'
import { loadSessionIdForBuilding } from './connector-dispatch-space'
import { getSessionRehome } from './connector-session-rehome-registry'
import type { PowerSyncWriteDb } from './powersync-db'

/**
 * Resolve the live capture session a 404'd count should re-home onto. The
 * orphan session id frozen into the count's op data after a parent-session 409
 * re-home is absent server-side, and the in-batch sessionIdRewrites map is empty
 * once the create and this count split across CRUD batches (a >100-op offline
 * session or an app reload). Recover it the two cross-batch ways spaces/floors
 * already do:
 *
 *   1. The DURABLE session re-home registry (H5 #701). getSessionRehome carries
 *      the orphan→authoritative mapping recorded when the parent session 409
 *      re-homed — durable, so it survives an app reload (hydrateSessionRehomes
 *      refills the cache each upload cycle). Keyed on the globally-unique orphan
 *      session id, so it is precise; tried first. The per-batch sessionIdRewrites
 *      map cannot reach it cross-batch. Mirrors connector-dispatch-space.ts /
 *      connector-dispatch-floor.ts.
 *   2. The building's live session (loadSessionIdForBuilding) with
 *      includeCompleted=true (H5 #701 / #664). The authoritative session the
 *      surveyor's children re-homed onto is frequently already COMPLETE (an
 *      admin force-complete, or a normal completion during the offline window).
 *      The default open-only lookup filtered it out → recovered=null → the count
 *      was QUARANTINED instead of recovered. spaces/floors already pass true.
 *
 * Returns the recovered session id, or null when none resolves (the caller then
 * quarantines — never silently drops). The caller skips a self-retry when the
 * recovered id equals the one already attempted.
 */
export async function recoverSessionForItem(
  db: PowerSyncWriteDb,
  context: DispatchContext,
  data: Record<string, unknown>,
  sessionId: unknown,
): Promise<string | null> {
  if (typeof sessionId === 'string' && sessionId !== '') {
    const rehomed = getSessionRehome(sessionId)?.authoritativeId ?? null
    if (rehomed !== null) return rehomed
  }
  const buildingId = data['building_id']
  if (typeof buildingId === 'string' && buildingId !== '') {
    return loadSessionIdForBuilding(db, buildingId, context, true)
  }
  return null
}
